import {
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { Check, Error as ErrorIcon, Info } from '@mui/icons-material'
import * as Sentry from '@sentry/react'
import Grid from '@mui/material/Grid2'
import { type JSX, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { object, string } from 'yup'
import type { Device } from '@/pages/Device/Device'
import config from '@/config/WebSocket'
import {
  calibrationCard,
  calibrationCardActions,
  warningCardHeader,
} from '@/utils/theme'
import useWebSocket from '@/hooks/useWebSocket'
import CircularProgressWithLabel from '@/components/CircularProgressWithLabel'

interface Props {
  device: Device | null
  onBack: () => void
  onNext: () => void
}

interface Instruction {
  title: string
  descr: string
  thingsToTry?: string[]
}

const messageSchema = object().shape({
  type: string().required(),
})

const iconContainerSx = { justifyContent: 'center', minWidth: '56px' } as const
const iconInfoSx = { color: '#00acc1' } as const
const iconSuccessSx = { color: '#4caf50' } as const
const iconErrorSx = { color: '#f44336' } as const
const thingsToTryItemSx = { paddingBottom: 0 } as const

const CalibrationStep = ({ device, onBack, onNext }: Props): JSX.Element => {
  const { t } = useTranslation('app')

  const [url, setUrl] = useState<string | null>(null)
  const [running, setRunning] = useState(false)
  const [stopping, setStopping] = useState(false)
  const [instruction, setInstruction] = useState<Instruction | null>(null)
  const [progress, setProgress] = useState(0)
  const [complete, setComplete] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  const receivedStopConfirmRef = useRef(false)

  const onClose = useCallback(() => {
    setRunning(false)
    setStopping(false)
    receivedStopConfirmRef.current = false
  }, [])

  const onError = useCallback(() => {
    setRunning(false)
    setStopping(false)
    setInstruction(null)
    setProgress(0)
    receivedStopConfirmRef.current = false
  }, [])

  const { sendJsonMessage, lastJsonMessage } = useWebSocket(url, {
    onClose,
    onError,
  })

  const increaseProgress = useCallback(() => {
    setProgress((prev) => prev + 25)
  }, [])

  const checkProgress = useCallback(() => {
    if (progress !== 100) return
    setShowSuccess(true)
  }, [progress])

  const start = useCallback(() => {
    if (!device) return
    receivedStopConfirmRef.current = false
    setUrl(`${config.url}rdi/${device.uid}/calibrate?t=${Date.now()}`)
    setProgress(0)
    setRunning(true)
    setComplete(false)
    setShowSuccess(false)
    setInstruction({
      title: t('device.calibration.calibrateStep.instructions.started.title'),
      descr: t('device.calibration.calibrateStep.instructions.started.descr'),
    })
  }, [device, t])

  const stop = useCallback(() => {
    setStopping(true)
    sendJsonMessage({ type: 'stop' })
  }, [sendJsonMessage])

  // Cleanup websocket on unmount
  useEffect(() => {
    return () => {
      setUrl(null)
    }
  }, [])

  // Handle incoming websocket messages
  useEffect(() => {
    if (!lastJsonMessage) return

    try {
      const validated = messageSchema.validateSync(lastJsonMessage)
      const msgType = validated.type

      switch (msgType) {
        case 'notStarted':
          setInstruction({
            title: t(
              'device.calibration.calibrateStep.instructions.notStarted.title',
            ),
            descr: t(
              'device.calibration.calibrateStep.instructions.notStarted.descr',
            ),
            thingsToTry: t(
              'device.calibration.calibrateStep.instructions.thingsToTryWhenNotStarted',
              { returnObjects: true },
            ) as string[],
          })
          break

        case 'startAtCwEnd':
          setInstruction({
            title: t(
              'device.calibration.calibrateStep.instructions.startAtCwEnd.title',
            ),
            descr: t(
              'device.calibration.calibrateStep.instructions.startAtCwEnd.descr',
            ),
          })
          increaseProgress()
          break

        case 'calCwEnd':
          setInstruction({
            title: t(
              'device.calibration.calibrateStep.instructions.calCwEnd.title',
            ),
            descr: t(
              'device.calibration.calibrateStep.instructions.calCwEnd.descr',
            ),
          })
          increaseProgress()
          break

        case 'calCcwEnd':
          setInstruction({
            title: t(
              'device.calibration.calibrateStep.instructions.calCcwEnd.title',
            ),
            descr: t(
              'device.calibration.calibrateStep.instructions.calCcwEnd.descr',
            ),
          })
          increaseProgress()
          break

        case 'notCalibrated':
          setInstruction({
            title: t(
              'device.calibration.calibrateStep.instructions.notCalibrated.title',
            ),
            descr: t(
              'device.calibration.calibrateStep.instructions.notCalibrated.descr',
            ),
            thingsToTry: t(
              'device.calibration.calibrateStep.instructions.thingsToTryWhenNotCalibrated',
              { returnObjects: true },
            ) as string[],
          })
          break

        case 'calibrationStopped':
          receivedStopConfirmRef.current = true
          setInstruction({
            title: t(
              'device.calibration.calibrateStep.instructions.calibrationStopped.title',
            ),
            descr: t(
              'device.calibration.calibrateStep.instructions.calibrationStopped.descr',
            ),
            thingsToTry: t(
              'device.calibration.calibrateStep.instructions.thingsToTryOnError',
              { returnObjects: true },
            ) as string[],
          })
          setStopping(false)
          break

        case 'calibrated':
          receivedStopConfirmRef.current = true
          setInstruction({
            title: t(
              'device.calibration.calibrateStep.instructions.calibrated.title',
            ),
            descr: t(
              'device.calibration.calibrateStep.instructions.calibrated.descr',
            ),
          })
          setComplete(true)
          // Clear progress errors that may confuse user after calibration completes
          increaseProgress()
          break

        default:
          break
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  }, [lastJsonMessage, increaseProgress, t])

  if (!device) return <></>

  const { type, serial, label, editable } = device
  const headerTitle = label ? `${serial} - ${label}` : serial
  const hasThingsToTry = Boolean(instruction?.thingsToTry?.length)

  const instructionIcon = (): JSX.Element => {
    if (hasThingsToTry) {
      return (
        <ErrorIcon
          sx={iconErrorSx}
          aria-label={t('device.calibration.calibrateStep.failure')}
        />
      )
    }
    if (showSuccess) {
      return (
        <Check
          sx={iconSuccessSx}
          aria-label={t('device.calibration.calibrateStep.success')}
        />
      )
    }
    return (
      <CircularProgressWithLabel
        variant={progress ? 'determinate' : 'indeterminate'}
        value={progress}
        size={40}
        aria-label="calibration-progress"
        onTransitionEnd={checkProgress}
        id="rdiCalibrateProgress"
      />
    )
  }

  return (
    <>
      {/* Card */}
      <Box sx={calibrationCard}>
        {/* Warning header */}
        <Box sx={warningCardHeader}>
          <Typography
            component="h4"
            sx={{
              color: '#fff',
              fontWeight: 300,
              fontSize: '18px',
              lineHeight: '1.375rem',
              margin: '0 0 5px 0',
            }}
          >
            {headerTitle}
          </Typography>
          <Typography
            component="h4"
            sx={{
              color: '#fff',
              fontWeight: 300,
              fontSize: '18px',
              lineHeight: '1.375rem',
              margin: 0,
            }}
          >
            {type}
          </Typography>
        </Box>

        <Box sx={{ padding: '0.9375rem 2px', mt: 1 }}>
          <Grid container>
            <Grid
              size={{ xs: 12, lg: 12 }}
              sx={{ padding: '0 15px !important' }}
            >
              <Button
                variant="contained"
                onClick={running ? stop : start}
                disabled={stopping}
                sx={{
                  mb: 2,
                  mt: 1,
                  backgroundColor: running ? '#f44336' : '#9c27b0',
                  '&:hover': {
                    backgroundColor: running ? '#d32f2f' : '#7b1fa2',
                  },
                }}
              >
                {running
                  ? t('device.calibration.calibrateStep.stopCalibration')
                  : t('device.calibration.calibrateStep.startCalibration')}
              </Button>

              <List>
                {/* Main instruction item */}
                {instruction && (
                  <ListItem>
                    <ListItemIcon sx={iconContainerSx}>
                      {instructionIcon()}
                    </ListItemIcon>
                    <ListItemText
                      primary={instruction.title}
                      secondary={instruction.descr}
                    />
                  </ListItem>
                )}

                {/* Things to try header */}
                {hasThingsToTry && (
                  <>
                    <ListItem>
                      <ListItemIcon sx={iconContainerSx}>
                        <Info sx={iconInfoSx} />
                      </ListItemIcon>
                      <ListItemText>
                        {t(
                          'device.calibration.calibrateStep.instructions.thingsToTry',
                        )}
                      </ListItemText>
                    </ListItem>
                    <Divider />
                  </>
                )}

                {/* Things to try list */}
                {hasThingsToTry &&
                  instruction?.thingsToTry?.map((item) => (
                    <ListItem key={item} sx={thingsToTryItemSx}>
                      <ListItemText inset secondary={item} />
                    </ListItem>
                  ))}
              </List>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={calibrationCardActions}>
        <Button
          variant="contained"
          onClick={onBack}
          data-testid="calibration-back-button"
        >
          {t('common:general.back')}
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={onNext}
          disabled={!editable || running || !complete}
          data-testid="calibration-next-button"
        >
          {t('common:general.next')}
        </Button>
      </Box>
    </>
  )
}

export default CalibrationStep
